import React, { useMemo, forwardRef } from 'react';
import { Card, Box, Typography } from '@mui/material';
import { Bar } from 'react-chartjs-2';
import { BASE_OPTIONS, EJE_X_BASE, EJE_Y_BASE } from './chartBase';
import { COLOR, FUENTE } from './tokens';
import InfoGrafico from './InfoGrafico';
import { MOCK_TURNOS_POR_DIA } from './mockDataEjemplos';

function formatearFechaCorta(iso) {
    if (!iso) return '';
    const [anio, mes, dia] = iso.split('-');
    return `${dia}/${mes}`;
}

/**
 * Turnos atendidos por día — serie diaria (a diferencia de la tendencia mensual), útil
 * para ver el detalle día a día dentro de un rango corto (una semana, un mes). Con
 * rangos largos (varios meses) el eje se satura — se recomienda un rango de hasta ~60
 * días; para períodos más largos conviene la tendencia mensual.
 * @param {{data: Array<{fecha: string, total: number, atendidos: number}>}} props
 */
const TurnosPorDiaChart = forwardRef(({ data, compacto = false }, ref) => {
    const filas = useMemo(() => data || [], [data]);
    const rangoLargo = filas.length > 60;

    const chartData = {
        labels: filas.map((f) => formatearFechaCorta(f.fecha)),
        datasets: [
            { label: 'Atendidos', data: filas.map((f) => f.atendidos), backgroundColor: COLOR.azul, borderRadius: 3, barThickness: rangoLargo ? undefined : 14 },
            { label: 'Total', data: filas.map((f) => f.total - f.atendidos), backgroundColor: COLOR.gris, borderRadius: 3, barThickness: rangoLargo ? undefined : 14, stack: 't' },
        ],
    };
    // "Atendidos" y "Total - atendidos" se apilan para que la altura total de la barra
    // muestre el total de turnos del día, con la porción atendida destacada en azul.
    chartData.datasets[0].stack = 't';

    const options = {
        ...BASE_OPTIONS,
        scales: {
            x: {
                ...EJE_X_BASE,
                stacked: true,
                ticks: {
                    ...EJE_X_BASE.ticks,
                    autoSkip: true,
                    maxRotation: 0,
                    maxTicksLimit: rangoLargo ? 12 : 31,
                },
            },
            y: { ...EJE_Y_BASE, stacked: true },
        },
    };

    return (
        <Card variant="outlined" sx={{
            position: compacto ? 'static' : 'relative', border: compacto ? 'none' : '1px solid #e2e8f0',
            borderRadius: compacto ? 0 : '12px', boxShadow: 'none', padding: compacto ? 0 : '20px 22px',
        }}>
            {!compacto && (
                <>
                    <InfoGrafico
                        titulo="Turnos por día"
                        queMuestra="Te da el detalle día por día del período — útil para ver picos y valles puntuales que un promedio mensual disimula, dentro de un rango corto (una semana, un mes)."
                        ejemploVisual={<TurnosPorDiaChart compacto data={MOCK_TURNOS_POR_DIA} />}
                        comoLeerlo={[
                            'Cada barra es un día; la altura total es el total de turnos agendados ese día.',
                            'La porción azul (Atendidos) es lo que efectivamente se atendió; la porción gris arriba es la diferencia contra el total agendado.',
                            'No hay color de alarma acá — es una serie descriptiva, se lee comparando barras entre sí, no contra un umbral fijo.',
                        ]}
                        analisis="El 3 de septiembre se atendieron muy pocos turnos (9 de 17 agendados, 53%) comparado con el resto de los días del ejemplo, que rondan el 60-73% de efectividad. Si ese día coincide con un feriado, una capacitación o una baja de personal, explica el bache; si no hay una razón así, vale la pena revisar qué pasó puntualmente ese día antes de que se repita."
                    />
                    <Typography sx={{ fontFamily: FUENTE, fontSize: 16, fontWeight: 700, color: COLOR.negro }}>Turnos por día</Typography>
                    <Typography sx={{ fontFamily: FUENTE, fontSize: 12.5, color: COLOR.grisTexto, marginBottom: '14px' }}>
                        Detalle día a día del período — la altura de la barra es el total de turnos del día y la porción azul los que se presentaron.
                    </Typography>
                </>
            )}
            {filas.length === 0 ? (
                <Typography sx={{ fontFamily: FUENTE, fontSize: 13, color: COLOR.grisTexto }}>
                    No hay turnos en el período seleccionado.
                </Typography>
            ) : (
                <Box sx={{ height: compacto ? 150 : 260 }}>
                    <Bar ref={ref} data={chartData} options={options} />
                </Box>
            )}
        </Card>
    );
});

export default TurnosPorDiaChart;
